import { ReactElement, useState, useEffect } from 'react';
import { Modal, Button, Input, Form, message } from "antd";
import { CardElement, Elements, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import firebase from "firebase/app";
import { v4 as uuidv4 } from "uuid";
import { stripePublicKey } from "../constants/stripe";
import api from "../redux/api/axiosInstance";

export const CheckoutForm = ({ amount, user, currency, booking, onDone }: any): ReactElement => {
	const stripe = useStripe();
	const elements = useElements();
	const [email, setEmail] = useState<string>(user.email);
	const [name, setName] = useState<string>(user.fullName);
	const [phone, setPhone] = useState<string>(user.phoneNumber);
	const [loading, setLoading] = useState<boolean>(false);
	const [paymentRequest, setPaymentRequest] = useState<any>(null);

	useEffect(() => {
		if (!stripe || !elements) {
			return;
		}

		// for apple pay
		const pr = stripe?.paymentRequest({
			currency: currency.toLowerCase(),
			country: "US",
			requestPayerEmail: true,
			requestPayerName: true,
			total: {
				label: "Create new booking",
				amount: amount * 100,
			}
		});

		pr?.canMakePayment().then(result => {
			if (result) {
				setPaymentRequest(pr);
			}
		})
			.catch(e => {
				console.log("Error:", e);
			})

		pr.on("paymentmethod", async (e) => {
			api.post("create_payment_intent", {
				amount: amount * 100,
				currency,
				description: "",
				paymentMethodType: "card"
			}).then(res => {
				const { clientSecret, success } = res.data;
				if (success) {
					stripe.confirmCardPayment(
						clientSecret,
						{ payment_method: e.paymentMethod.id },
						{ handleActions: false }
					)
						.then(({ error, paymentIntent }: any) => {
							const paymentHistoryDocId = uuidv4();
							if (error) {
								e.complete('fail');
								firebase.firestore().collection("payment_history").doc(paymentHistoryDocId).set({
									userId: user.id,
									email,
									name,
									phone,
									amount,
									currency,
									transactionId: error.payment_intent ? error.payment_intent.id : "",
									clubId: booking.club.clubId,
									branchId: booking.branch.branchId,
									bookingDate: booking.bookingDate,
									bookingTime: booking.bookingTime,
									bookingDuration: booking.duration,
									description: '',
									status: "fail",
									error: error.raw,
									paymentType: "apple",
									timestamp: firebase.firestore.FieldValue.serverTimestamp()
								});
								return;
							}
							e.complete('success');
							if (paymentIntent.status === "requires_action") {
								stripe.confirmCardPayment(clientSecret);
							}
							const transactionId = paymentIntent.id;
							firebase.firestore().collection("payment_history").doc(paymentHistoryDocId).set({
								userId: user.id,
								email,
								name,
								phone,
								amount,
								currency,
								transactionId,
								clubId: booking.club.firestoreRef,
								branchId: booking.branch.firestoreRef,
								bookingDate: booking.bookingDate,
								bookingTime: booking.bookingTime,
								bookingDuration: booking.duration,
								description: '',
								status: "processed",
								paymentType: "apple",
								timestamp: firebase.firestore.FieldValue.serverTimestamp()
							});
							message.success(res.data.message);
							setTimeout(() => {
								onDone(transactionId);
							}, 1000);
						})
				} else {
					message.error("Something went wrong!");
				}
			})
		});
	}, [stripe, elements])

	// Create Card Payment 

	const handleSubmit = async (event: any) => {
		event.preventDefault();
		setLoading(true);
		let card;
		// Check the stripe ready
		if (elements === null) {
			message.error("Oop! Something went wrong, please try to do later!");
			return;
		}
		// Check if stripe card element ready
		const cardElement = elements.getElement(CardElement);
		if (cardElement === null) {
			message.error("Oop! Something went wrong, please try to do later!");
			return;
		}
		// Create payment method
		try {
			const { error, paymentMethod }: any = await stripe?.createPaymentMethod({
				type: 'card',
				card: cardElement,
				billing_details: {
					email, phone, name
				}
			});
			card = paymentMethod;
			// Check if create payment method is failed
			if (error) {
				console.log(error);
				message.error(error.message)
				setLoading(false);
				return;
			}
		} catch (e) {
			console.log(e);
			message.error("Oop! Something went wrong, please try to do later!");
			setLoading(false);
			return;
		}
		// Create Payment
		const { id } = card;
		const paymentData = {
			payment_method: id,
			amount: amount * 100,
			currency,
			email,
			name,
			phone,
			userId: user.id,
			clubId: booking.club.firestoreRef,
			branchId: booking.branch.firestoreRef,
			bookingDate: booking.bookingDate,
			bookingTime: booking.bookingTime,
			bookingDuration: booking.duration,
			description: "",
		}
		api.post("/create_payment", paymentData)
			// api.post("/create_payment_test", paymentData)
			.then(res => {
				console.log("Result:", res);
				if (res.data.success) {
					const { transactionId } = res.data;
					message.success(res.data.message);
					setTimeout(() => {
						onDone(transactionId);
					}, 1000);
				} else {
					message.error(res.data.message);
				}
				setLoading(false);
			})
			.catch(err => {
				message.error("Oop! something went wrong, please try again later");
				console.log(err);
				setLoading(false);
			})
	}

	const onChangeName = (event: any) => {
		setName(event.target.value);
	}
	const onChangeEmail = (event: any) => {
		setEmail(event.target.value);
	}
	const onChangePhone = (event: any) => {
		setPhone(event.target.value);
	}

	return (
		<form onSubmit={handleSubmit} className="checkout-form">
			<h3 className="booking-header-title">Payment Detail</h3>
			<Form.Item
				label="Card holder name"
				name="holderName"
				rules={[{
					required: true,
					message: "Card holder name is required"
				}]}
				labelCol={{ span: 24 }}
			>
				<Input placeholder="Card holder name" required onChange={onChangeName} defaultValue={user.fullName} />
			</Form.Item>
			<Form.Item
				label="Email Address"
				name="email"
				rules={[
					{
						required: true,
						message: "Email address is required"
					},
					{
						type: "email",
						message: "Please enter vaild email"
					}
				]}
				labelCol={{ span: 24 }}
			>
				<Input type="email" placeholder="Email" required onChange={onChangeEmail} defaultValue={user.email} />
			</Form.Item>
			<Form.Item
				label="Phone number"
				name="phone"
				rules={[
					{
						required: true,
						message: "Phone number is required"
					},
				]}
				labelCol={{ span: 24 }}
			>
				<Input placeholder="Phone number" type="tel" required onChange={onChangePhone} defaultValue={user.phoneNumber} />
			</Form.Item>
			<Form.Item
				label="Credit Card"
				labelCol={{ span: 24 }}
				required
				className="stripe-card-input-container"
			>
				<CardElement />
			</Form.Item>
			<Button className="pay-button" type="primary" htmlType="submit" loading={loading} style={{ marginBottom: 10 }}>
				Pay now
			</Button>
			{paymentRequest && <PaymentRequestButtonElement options={{ paymentRequest }} />}
		</form>
	)

}

const Checkout = ({ visible, user, booking, onDone, onCancel, amount, currency }: any): ReactElement => {
	const stripePromise = loadStripe(stripePublicKey);
	return (
		<Elements stripe={stripePromise}>
			<CheckoutForm
				amount={amount}
				currency={currency}
				onDone={onDone}
				user={user}
				booking={booking}
			/>
		</Elements>
	)
}

export default Checkout
